import { useState } from 'react';
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  FloatingPortal,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react';
import { Avatar, MyLink } from '@scramble/ui';
import IconChevron from '@scramble/design-system/icons/chevron-selector-vertical.svg?react';
import type { UserMenuProps } from '../types';
import s from './index.module.scss';

export const UserMenu = ({ user, dropdownMenu }: UserMenuProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const card = (
    <>
      <div className={s.avatar}>
        <Avatar url={user.avatar} name={user.name} />
      </div>
      <div className={s.content}>
        <div className="f-body-sm f-500">{user.name}</div>
        <div className="f-body-sm">{user.id}</div>
      </div>
      <IconChevron className={s.chevron} />
    </>
  );

  // Without a dropdown the card is a plain link, as it was before the dropdown existed:
  // an app that only passes `user.link` (apps/scramble-investors) keeps a working
  // route to its profile instead of a button that opens nothing.
  if (!dropdownMenu && user.link) {
    return (
      <MyLink href={user.link} className={s.wrapper}>
        {card}
      </MyLink>
    );
  }

  return (
    <>
      <button ref={refs.setReference} className={s.wrapper} {...getReferenceProps()}>
        {card}
      </button>

      {isOpen && dropdownMenu && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={s.dropdown}
            {...getFloatingProps()}
          >
            {dropdownMenu}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
